import React from 'react';
import { useRegisterTempBadgeMutation } from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { getGraphQLError } from 'utils/error';
import { useIsMounted } from 'utils';
import { FormOnSubmit, ModalFormContent } from 'components/form';
import { TempBadge } from 'containers/jobsite/tabs/jobsiteTempBadges/types';
import { RegisterTempBadgeFormData } from './types';
import {
  getFormDefaultValues,
  getFormInputsHook,
  useCardFormatOptions,
} from './RegisterTempBadgeModalContent.forms';

export interface RegisterTempBadgeModalContentProps {
  closeModal: () => void;
  onAction: (tempBadge: TempBadge) => void;
  jobsiteId: string;
  jobsiteName: string;
  tempBadge: TempBadge;
}

const instructions =
  'Enter the badge number printed on the back of the card near the bottom edge.';

export function RegisterTempBadgeModalContent(
  props: RegisterTempBadgeModalContentProps,
): React.ReactElement {
  const { jobsiteId, tempBadge, closeModal, onAction } = props;
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const isMounted = useIsMounted();

  const [registerTempBadge] = useRegisterTempBadgeMutation();

  const onSubmit: FormOnSubmit<RegisterTempBadgeFormData> = async (
    data,
    // event,
    // dirtyFields,
    // formApi,
  ): Promise<void> => {
    if (isFetching) return;
    setIsFetching(true);

    try {
      const { data: result } = await registerTempBadge({
        variables: {
          input: {
            id: tempBadge.id,
            cardNumber: data.badgeNumber,
            jobsiteCardFormatId: data.jobsiteCardFormatId.value,
          },
        },
      });
      const registeredTempBadge = result.registerTempBadge.tempBadge;
      onAction(registeredTempBadge);
    } catch (error) {
      AlertService.alert(
        'danger',
        'Error registering temp badge',
        getGraphQLError(error),
      );
    } finally {
      if (isMounted()) setIsFetching(false);
    }
  };

  const { cardFormatOptions, defaultCardFormatOption } =
    useCardFormatOptions(jobsiteId);

  const formInputs = React.useMemo(
    () => getFormInputsHook({ cardFormatOptions }),
    [cardFormatOptions],
  );
  const defaultValues = React.useMemo(
    () => getFormDefaultValues({ cardFormatOptions, defaultCardFormatOption }),
    [cardFormatOptions, defaultCardFormatOption],
  );

  return (
    <ModalFormContent
      setOpen={closeModal}
      renderAbove={
        <div>
          <p className="odin-mb-4">{instructions}</p>
        </div>
      }
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Register"
      actionButtonWithSpinner={isFetching}
    />
  );
}
